"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useParams } from "next/navigation";
import { Button, ImagePreview, Rate, Skeleton } from "react-vant";
import { Arrow, ArrowLeft, Down } from "@react-vant/icons";
import BreadCrumb from "@/components/global/BreadCrumb";
import TabBar from "@/components/global/TabBar";
import ProductItem from "@/components/global/ProductItem";
import productApi from "@/service/modules/productApi";
import { formatCurrency, paginationChange } from "@/utils/utils";
import styles from "@/styles/product-reviews/product-reviews-page.module.scss";
import type {
  BreadCrumbItem,
  CustomerReviewData,
  PictureItem,
  ProductType,
  ReviewItem,
} from "@/types/product";

type ProductReviewDetail = ProductType & {
  breadCrumb?: BreadCrumbItem[];
};

type OpenPreview = (images: string[], index: number) => void;

const defaultCustomerData: CustomerReviewData = {
  review: [],
  reviewAvgRank: 0,
  reviewCount: 0,
  reviewSummary: {
    rating1: 0,
    rating2: 0,
    rating3: 0,
    rating4: 0,
    rating5: 0,
  },
};

const sortOptions = [
  { label: "Date - Newest First", value: "newest" },
  { label: "Date - Oldest First", value: "oldest" },
  { label: "Rating - High to Low", value: "high" },
  { label: "Rating - Low to High", value: "low" },
  { label: "Helpfulness - High to Low", value: "helpfulness" },
];

export default function ProductReviewsPage() {
  const params = useParams<{ id: string }>();
  const productId = params.id;

  const [activeTab, setActiveTab] = useState(0);
  const [productDetail, setProductDetail] =
    useState<ProductReviewDetail | null>(null);
  const [customerData, setCustomerData] =
    useState<CustomerReviewData>(defaultCustomerData);
  const [sort, setSort] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(6);
  const [productLoading, setProductLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [previewIndex, setPreviewIndex] = useState(0);

  const pictures = useMemo(
    () => productDetail?.pictures || [],
    [productDetail?.pictures],
  );

  const totalPages = Math.max(
    1,
    Math.ceil((customerData.reviewCount || 0) / pageSize),
  );

  const tabList = [
    { id: 1, name: `Customer Reviews(${customerData.reviewCount || 0})` },
    { id: 2, name: `Images(${pictures.length})` },
  ];

  useEffect(() => {
    if (!productId) return;

    const getProductDetail = async () => {
      try {
        setProductLoading(true);
        const res = await productApi.getProductDetail(productId);
        if (res.data?.product) {
          const breadCrumb = Array.isArray(res.data.breadCrumb)
            ? res.data.breadCrumb.map((item: BreadCrumbItem) => ({
                ...item,
                current: false,
              }))
            : [];
          const lastBreadcrumbItem = breadCrumb[breadCrumb.length - 1];
          if (lastBreadcrumbItem) {
            breadCrumb.push({
              ...lastBreadcrumbItem,
              name: `${lastBreadcrumbItem.name} Reviews`,
              url: `/product/${productId}/reviews`,
              current: true,
            });
          }

          setProductDetail({
            ...res.data.product,
            breadCrumb,
          });
        }
      } catch (error) {
        console.log("fetch product review detail error", error);
      } finally {
        setProductLoading(false);
      }
    };

    getProductDetail();
  }, [productId]);

  useEffect(() => {
    if (!productId) return;

    const getReviews = async () => {
      try {
        setReviewsLoading(true);
        const range = paginationChange(page, pageSize);
        const res = await productApi.getCustomerReviews({
          productID: productId,
          sort,
          start: range.start,
          rows: range.end,
        });
        if (res.data) {
          setCustomerData(res.data);
        }
      } catch (error) {
        console.log("fetch product reviews error", error);
      } finally {
        setReviewsLoading(false);
      }
    };

    getReviews();
  }, [page, pageSize, productId, sort]);

  const productUrl = productDetail?.url || "";
  const productImage =
    productDetail?.newThumbnail ||
    productDetail?.smallThumbnail ||
    productDetail?.thumbnail ||
    productDetail?.filename ||
    "";

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSort(event.target.value);
    setPage(1);
  };

  const openPreview: OpenPreview = (images, index) => {
    setPreviewImages(images);
    setPreviewIndex(index);
    setPreviewVisible(true);
  };

  return (
    <div className={styles["page-product-reviews"]}>
      <section className={styles["product-panel"]}>
        {productLoading ? (
          <div className={styles["skeleton-box"]}>
            <Skeleton />
            <Skeleton style={{ marginTop: 16 }} />
          </div>
        ) : productDetail ? (
          <>
            <div className={styles["breadcrumb"]}>
              <BreadCrumb BreadList={productDetail.breadCrumb?.slice(-2)} />
            </div>

            <div className={styles["product-card"]}>
              <Link href={productUrl} className={styles["product-image-link"]}>
                {!!productImage && (
                  <Image
                    src={productImage}
                    alt={productDetail.name}
                    width={132}
                    height={132}
                    className={styles["product-image"]}
                  />
                )}
              </Link>

              <div className={styles["product-info"]}>
                <Link href={productUrl} className={styles["product-title"]}>
                  {productDetail.name}
                </Link>

                <div className={styles["price-row"]}>
                  <span className={styles["current-price"]}>
                    {formatCurrency(productDetail.price || 0)}
                  </span>
                  {!!productDetail.normalPrice && (
                    <span className={styles["normal-price"]}>
                      {formatCurrency(productDetail.normalPrice)}
                    </span>
                  )}
                </div>

                <div className={styles["rating-row"]}>
                  <Rate
                    value={customerData.reviewAvgRank || 0}
                    count={5}
                    color="#FBC72E"
                    voidColor="#dedede"
                    allowHalf
                    readOnly
                    size={15}
                  />
                  <span>({customerData.reviewCount || 0})</span>
                </div>

                <Link href={productUrl}>
                  <Button type="primary" className={styles["buy-now"]}>
                    Buy Now
                  </Button>
                </Link>
              </div>
            </div>
          </>
        ) : (
          <div className={styles["empty-box"]}>Product not found.</div>
        )}
      </section>

      <section className={styles["reviews-panel"]}>
        <TabBar
          tabList={tabList}
          activeTab={activeTab}
          onTabChange={(index) => setActiveTab(index)}
          tabItemRightMargin="18px"
        />

        {activeTab === 0 && (
          <CustomerReviewsTab
            productId={productId}
            customerData={customerData}
            loading={reviewsLoading}
            sort={sort}
            totalPages={totalPages}
            page={page}
            onSortChange={handleSortChange}
            onPageChange={setPage}
            onPreview={openPreview}
          />
        )}

        {activeTab === 1 && (
          <ImagesTab pictures={pictures} onPreview={openPreview} />
        )}
      </section>

      <SimilarProductsGrid productId={productId} />

      <ImagePreview
        visible={previewVisible}
        images={previewImages}
        startPosition={previewIndex}
        onClose={() => setPreviewVisible(false)}
      />
    </div>
  );
}

function CustomerReviewsTab({
  productId,
  customerData,
  loading,
  sort,
  totalPages,
  page,
  onSortChange,
  onPageChange,
  onPreview,
}: {
  productId: string;
  customerData: CustomerReviewData;
  loading: boolean;
  sort: string;
  totalPages: number;
  page: number;
  onSortChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onPageChange: (page: number) => void;
  onPreview: OpenPreview;
}) {
  return (
    <div className={styles["customer-tab"]}>
      <ReviewControls
        productId={productId}
        sort={sort}
        onSortChange={onSortChange}
      />

      {loading ? (
        <div className={styles["skeleton-box"]}>
          <Skeleton />
          <Skeleton style={{ marginTop: 16 }} />
        </div>
      ) : customerData.review.length ? (
        <div className={styles["review-list"]}>
          {customerData.review.map((item, index) => (
            <ReviewCard
              item={item}
              key={`${item.createTime}-${index}`}
              onPreview={onPreview}
            />
          ))}
        </div>
      ) : (
        <ReviewEmpty productId={productId} />
      )}

      {customerData.reviewCount > 0 && (
        <div className={styles["pagination-row"]}>
          <button
            type="button"
            className={styles["page-btn"]}
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            <ArrowLeft />
          </button>
          <span className={styles["page-count"]}>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className={styles["page-btn"]}
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            <Arrow />
          </button>
        </div>
      )}
    </div>
  );
}

function ReviewControls({
  productId,
  sort,
  onSortChange,
}: {
  productId: string;
  sort: string;
  onSortChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}) {
  return (
    <div className={styles["review-controls"]}>
      <Link href={`/review/write-review/${productId}`}>
        <Button className={styles["write-review"]}>Write a Review</Button>
      </Link>

      <label className={styles["sort-box"]}>
        <span>Sort by:</span>
        <select value={sort} onChange={onSortChange}>
          <option value="">Choose a sort order</option>
          {sortOptions.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <Down className={styles["sort-arrow"]} />
      </label>
    </div>
  );
}

function ReviewCard({
  item,
  onPreview,
}: {
  item: ReviewItem;
  onPreview: OpenPreview;
}) {
  return (
    <article className={styles["review-item"]}>
      <div className={styles["review-header"]}>
        <div className={styles["avatar"]} aria-hidden="true">
          <span className={styles["avatar-head"]}></span>
          <span className={styles["avatar-body"]}></span>
        </div>

        <div className={styles["review-meta"]}>
          <div className={styles["reviewer-name"]}>
            {item.customerName || "customer"}
          </div>
          <div className={styles["review-date"]}>{item.createTime}</div>
        </div>

        <Rate
          value={item.rate}
          count={5}
          color="#FBC72E"
          voidColor="#dedede"
          allowHalf
          readOnly
          size={15}
        />
      </div>

      <div className={styles["review-body"]}>
        {item.detail}
        {!!(item.imgArray && item.imgArray.length > 0) && (
          <div className={styles["review-images"]}>
            {item.imgArray.map((img, index) => (
              <button
                type="button"
                className={styles["review-image-btn"]}
                key={img}
                onClick={() => onPreview(item.imgArray, index)}
              >
                <Image
                  src={img}
                  width={80}
                  height={80}
                  alt="Review image"
                  className={styles["review-image"]}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

function ImagesTab({
  pictures,
  onPreview,
}: {
  pictures: PictureItem[];
  onPreview: OpenPreview;
}) {
  const images = pictures
    .map((item) => item.imageHDUrl || item.imageORUrl || item.fileNameUrl)
    .filter((src): src is string => Boolean(src));

  if (!images.length) {
    return <div className={styles["empty-box"]}>No product images yet.</div>;
  }

  return (
    <div className={styles["images-grid"]}>
      {images.map((src, index) => (
        <button
          type="button"
          className={styles["image-tile"]}
          key={src}
          onClick={() => onPreview(images, index)}
        >
          <Image
            src={src}
            width={160}
            height={160}
            alt="Product image"
            className={styles["tab-image"]}
          />
        </button>
      ))}
    </div>
  );
}

function ReviewEmpty({ productId }: { productId: string }) {
  return (
    <div className={styles["review-empty"]}>
      <Image
        src="https://img.bestdeals.co.nz/images/next/images/customerReviews.png"
        width={225}
        height={160}
        alt="Customer reviews"
        className={styles["review-empty-img"]}
      />
      <div className={styles["review-empty-title"]}>
        Share your review and photos here.
        <br />
        And 100-200 BestDealsPoints will be rewarded after the review approved.
      </div>
      <div className={styles["review-empty-note"]}>
        (100 BestDealsPoints Equals to $1)
      </div>
      <div className={styles["review-empty-desc"]}>
        Note: You will get double BestDealsPoints if your review is one of the
        first three approved!
      </div>
      <Link href={`/review/write-review/${productId}`}>
        <Button type="primary" className={styles["empty-review-btn"]}>
          Write a Review
        </Button>
      </Link>
    </div>
  );
}

function SimilarProductsGrid({ productId }: { productId: string }) {
  const [products, setProducts] = useState<ProductType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) return;

    const getRelatedProducts = async () => {
      try {
        setLoading(true);
        const res = await productApi.getProductScreen(productId);
        if (res.data?.relatedProducts?.length) {
          setProducts(res.data.relatedProducts);
        }
      } catch {
        console.log("relatedProducts fetch error");
      } finally {
        setLoading(false);
      }
    };

    getRelatedProducts();
  }, [productId]);

  if (loading) {
    return (
      <section className={styles["similar-products-section"]}>
        <div className={styles["skeleton-box"]}>
          <div className={styles["similar-title"]}>Similar Products</div>
          <Skeleton />
        </div>
      </section>
    );
  }

  if (!products.length) {
    return null;
  }

  return (
    <section className={styles["similar-products-section"]}>
      <div className={styles["skeleton-box"]}>
        <div className={styles["similar-title"]}>Similar Products</div>
      <div className={styles["similar-grid"]}>
        {products.map((item) => {
          const product = {
            ...item,
            filename:
              item.filename ||
              item.newThumbnail ||
              item.smallThumbnail ||
              item.thumbnail ||
              "",
          };

          return (
            <div className={styles["similar-item"]} key={item.productID}>
              <ProductItem product={product} />
            </div>
          );
        })}
      </div>
      </div>
    </section>
  );
}
